import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { PendingDocuments } from './Pages';
import { StatCard, Badge, PersonCell, fullName, workLabel, leaveLabel, AddedOn, BONUS_LABELS, Modal, NormCard, TeamDiffBadge, askRejectReason, overlapText, overlapShort, fmtLeaveDays, leaveLeft } from '../components/UI';
import api from '../utils/api';
import { SettlementSpec } from '../components/PaySpec';

const WEEK_LIMIT = 15; // T94: ile eventów pokazać w „Wszystkie" — w sezonie bywa ich 30 dziennie
const dayKey = (d) => String(d).slice(0, 10);
const localDay = (offset = 0) => { const d = new Date(); d.setDate(d.getDate() + offset); return d.toLocaleDateString('sv-SE'); };
/** „Dziś" / „Jutro" / „śr 15.10" */
const dayLabel = (d) => {
  const k = dayKey(d);
  if (k === localDay(0)) return 'Dziś';
  if (k === localDay(1)) return 'Jutro';
  return new Date(k + 'T12:00:00').toLocaleDateString('pl-PL', { weekday: 'short', day: '2-digit', month: '2-digit' });
};
/** Ostrzeżenia eventu dla koordynacji: odmowy, brak potwierdzeń, dokumenty. */
const eventFlags = (ev) => {
  const a = ev.assignments || [];
  return {
    declined: a.filter(x => x.declinedAt).length,
    unconfirmed: a.filter(x => !x.confirmedAt && !x.declinedAt).length,
    docs: ev.requiresDocs ? a.filter(x => !x.docsVerified).length : 0,
    team: a.length,
  };
};

/** T104: „Zmiany w składach" — odpowiedzi pracowników na eventy (dla układających składy). */
function TeamChanges() {
  const [all, setAll] = useState(false);
  const [rows, setRows] = useState(null);
  const load = () => api.getTeamChanges(all).then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [all]);
  const seen = async (ids) => { try { await api.markTeamChangesSeen(ids); load(); } catch (e) { window.alert(e.message); } };
  if (rows === null) return null;
  const unseen = rows.filter(r => !r.seenAt);
  if (!all && rows.length === 0) return null;
  const when = (d) => new Date(d).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  const kindTxt = { CONFIRMED: ['✓ potwierdził(a) udział', '#059669'], DECLINED: ['✗ nie może', '#DC2626'], DECLINED_AFTER_CONFIRM: ['✗ ODWOŁAŁ(A) po potwierdzeniu', '#B91C1C'], CONFIRMED_LATE: ['✓ potwierdził(a) PO TERMINIE', '#B45309'] }; // T164
  return (
    <div className="card" id="zmiany-w-skladach" style={{ border: '1px solid #FECACA' }}>
      <div className="flex gap-8 flex-wrap" style={{ alignItems: 'center', marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>🔔 Zmiany w składach{unseen.length ? ` (${unseen.length})` : ''}</h3>
        <label style={{ fontSize: 11, display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer', marginLeft: 8 }}>
          <input type="checkbox" checked={all} onChange={e => setAll(e.target.checked)} /> pokaż też zwykłe potwierdzenia i przejrzane
        </label>
        {unseen.length > 0 && <button className="btn btn-outline btn-sm" style={{ marginLeft: 'auto' }} onClick={() => seen(unseen.map(r => r.id))}>✓ Widziałem wszystkie</button>}
      </div>
      {rows.length === 0 ? <div style={{ fontSize: 12, color: '#94A3B8' }}>Brak zmian.</div> : (
        <table className="data-table" style={{ fontSize: 12 }}>
          <thead><tr><th>Kiedy</th><th>Pracownik</th><th>Co</th><th>Event</th><th>Dostępność</th><th></th></tr></thead>
          <tbody>
            {rows.map(r => {
              const [txt, color] = kindTxt[r.kind] || [r.kind, '#334155'];
              return (
                <tr key={r.id} style={r.seenAt ? { opacity: 0.55 } : undefined}>
                  <td style={{ whiteSpace: 'nowrap' }}>{when(r.respondedAt)}</td>
                  <td><b>{r.user.firstName} {r.user.lastName}</b></td>
                  <td style={{ color, fontWeight: 600 }}>{txt}{r.reason && <div style={{ fontWeight: 400, color: '#64748B', fontStyle: 'italic' }}>„{r.reason}”</div>}</td>
                  <td><Link to="/events">{r.event.name}</Link><div style={{ fontSize: 11, color: '#64748B' }}>{new Date(r.event.date).toLocaleDateString('pl-PL')}{r.event.startTime ? ` · ${r.event.startTime}–${r.event.endTime || '?'}` : ''}{r.event.location ? ` · ${r.event.location}` : ''}</div></td>
                  <td style={{ fontSize: 11 }}>{r.declaredAvailable
                    ? <span style={{ color: '#059669' }}>✓ zaznaczył(a) dostępność{r.availability?.fromTime ? ` (${r.availability.fromTime}–${r.availability.toTime})` : ''}</span>
                    : <span style={{ color: '#B45309' }}>⚠ nie zaznaczył(a) dostępności</span>}</td>
                  <td>{r.seenAt
                    ? <span style={{ fontSize: 10, color: '#94A3B8' }}>widział(a) {r.seenBy ? `${r.seenBy.firstName}` : ''}</span>
                    : <button className="btn btn-ghost btn-sm" onClick={() => seen([r.id])}>✓ widziałem</button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 6 }}>Odmowa nie zdejmuje pracownika ze składu — zmień skład w Eventach (✎ / 👥). Lista obejmuje eventy od wczoraj w przód.</div>
    </div>
  );
}

const dniowki = (n) => `${n} ${n === 1 ? 'dniówka' : [2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100) ? 'dniówki' : 'dniówek'}`;
/** T108: z czego składa się kwota — „za pracę 880 zł (8 h) + dodatki 50 zł” (pole z /settlements/mine: sum()). */
function payParts(s) {
  if (!s) return '';
  const work = [s.hoursTotal ? `${String(s.hoursTotal).replace('.', ',')} h` : null, s.dailyCount ? dniowki(s.dailyCount) : null].filter(Boolean).join(', ');
  return [
    s.hoursAmount || work ? `za pracę ${(s.hoursAmount || 0).toFixed(0)} zł${work ? ` (${work})` : ''}` : null,
    s.bonusAmount ? `dodatki ${s.bonusAmount.toFixed(0)} zł` : null,
  ].filter(Boolean).join(' + ');
}

/** T127: kto dziś nie pracuje (urlop, L4, zdalnie) — admin, kadry, rozliczający urlopy. */
function TodayAbsent() {
  const [rows, setRows] = useState(null);
  useEffect(() => { const d = new Date().toLocaleDateString('sv-SE'); api.getAbsences(d, d).then(r => setRows(r.filter(x => x.status !== 'PENDING'))).catch(() => setRows([])); }, []);
  if (!rows) return null;
  const icon = (t) => t === 'SICK' ? '🤒' : t === 'REMOTE_WORK' ? '💻' : '🏖';
  const until = (r) => new Date(r.endDate + 'T12:00:00').toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' });
  return (
    <div className="card">
      <div className="flex" style={{ alignItems: 'baseline', marginBottom: 6 }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>🏢 Dziś nieobecni ({rows.length})</h3>
        <Link to="/availability" style={{ marginLeft: 'auto', fontSize: 11 }}>kalendarz →</Link>
      </div>
      {rows.length ? (
        <div className="flex gap-8 flex-wrap" style={{ fontSize: 12 }}>
          {rows.map(r => <span key={r.id} title={leaveLabel(r.leaveType)}>{icon(r.leaveType)} <b>{r.user.firstName} {r.user.lastName}</b> <span style={{ color: '#94A3B8' }}>do {until(r)}</span></span>)}
        </div>
      ) : <div style={{ fontSize: 12, color: '#94A3B8' }}>Wszyscy w pracy.</div>}
    </div>
  );
}

/** Kafelek kwoty: zatwierdzone na czarno, niezatwierdzone szarym pod spodem (decyzja 07.10, T87) — oba z rozbiciem (T108). */
function PayCard({ label, approved, pending, accent }) {
  return (
    <div className="card stat-card" style={{ minWidth: 190 }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color: accent }}>{(approved?.amount || 0).toFixed(0)} zł</div>
      <div className="stat-sub">{payParts(approved) || 'nic zatwierdzonego'}</div>
      {pending?.amount > 0 && (
        <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }} title="Wpisy jeszcze niezatwierdzone — nie wchodzą do kwoty powyżej">
          + {pending.amount.toFixed(0)} zł czeka na zatwierdzenie<div>({payParts(pending)})</div>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user, isAdmin, isCoordinator, isManagement, isWorker, hasLeave, canAddHours, isEventOnly, refreshUser, canReviewHours, canApproveEventHours, canApproveLeaves, canAssignEvents, seesStaffAbsences, hasExpenses, canAddWarehouseHours } = useAuth();
  // T94: kto widzi eventy wszystkich (układa składy albo zatwierdza godziny) i kto sam jeździ na eventy
  const seesAllEvents = canAssignEvents || canApproveEventHours; // T118: samo zatwierdzanie godzin nie pokazuje eventów
  const hasEventWork = user?.role === 'EVENT' || !!user?.eventWorkEnabled;
  const [weekAll, setWeekAll] = useState([]);           // T94: eventy wszystkich, najbliższe 7 dni
  const [evTab, setEvTab] = useState(hasEventWork ? 'mine' : 'all');
  const [pendingBonuses, setPendingBonuses] = useState([]); // T77: dodatki/wydatki do zatwierdzenia
  const [showLastPay, setShowLastPay] = useState(false);    // T90: specyfikacja ostatniej wypłaty
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [pendingHours, setPendingHours] = useState([]);
  const [recentHours, setRecentHours] = useState([]);
  const [pay, setPay] = useState(null);                // T87: do wypłaty / ten miesiąc / ostatnia wypłata
  const [norm, setNorm] = useState(null);              // T98: norma i nadgodziny bieżącego miesiąca (magazyn)
  const [rejected, setRejected] = useState([]);       // T153: moje odrzucone godziny i dodatki (90 dni) — do poprawy
  const [toSettle, setToSettle] = useState([]);        // T86: odbyte eventy bez wpisu godzin
  const [upcoming, setUpcoming] = useState([]);        // najbliższe eventy, do których jestem przypisany (T48)
  const [inbox, setInbox] = useState([]);              // ostatnie wiadomości (T46 / T48)
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [leaves, hours] = await Promise.all([
          api.getLeaves('status=PENDING'),
          api.getHours('status=PENDING'),
        ]);
        // Do zatwierdzenia tylko CUDZE (własnych nikt nie zatwierdza sam sobie)
        setPendingLeaves(leaves.filter(l => l.userId !== user?.id && l.status === 'PENDING'));
        setPendingHours(hours.filter(h => h.userId !== user?.id && !h.settlementId));
        if (canReviewHours) api.getBonuses().then(b => setPendingBonuses(b.filter(x => x.status === 'PENDING' && x.userId !== user?.id && !x.settlementId))).catch(() => {});
        refreshUser && refreshUser(); // świeże saldo urlopu (T20)
        api.getInbox().then(list => setInbox(list.slice(0, 3))).catch(() => {});
        if (canAddHours) api.getMySettlements().then(setPay).catch(() => {});
        if (user?.role === 'WAREHOUSE') api.getMonthSummary(new Date().toLocaleDateString('sv-SE').slice(0, 7)).then(setNorm).catch(() => {});
        if (seesAllEvents) api.getEvents(`from=${localDay(0)}&to=${localDay(6)}`).then(evs => setWeekAll(evs.filter(e => e.status !== 'CANCELLED'))).catch(() => {});
        if (!isAdmin) { // T153: odrzucone — każdy widzi swoje, rzucające się w oczy
          const q = `from=${localDay(-90)}&to=${localDay(1)}`;
          Promise.all([api.getHours(q).catch(() => []), api.getBonuses(q).catch(() => [])]).then(([hs, bs]) => setRejected([
            ...hs.filter(h => h.userId === user?.id && h.status === 'REJECTED').map(h => ({ ...h, kind: 'hours' })),
            ...bs.filter(b => b.userId === user?.id && b.status === 'REJECTED').map(b => ({ ...b, kind: 'bonus' })),
          ].sort((a, b) => String(b.date).localeCompare(String(a.date)))));
        }
        if (isWorker) {
          // Tylko WŁASNE — osoba z Biura z prawem zatwierdzania dostaje z API też wpisy zespołu
          const myHours = (await api.getHours()).filter(h => h.userId === user?.id);
          setRecentHours(myHours.slice(0, 5));
          const now = new Date();
          if (user?.role === 'EVENT' || user?.eventWorkEnabled) {
            const from = localDay(0);
            const to = localDay(60);
            // mine=1: tylko eventy, w których jestem w składzie — także u widzących wszystkie (T94)
            api.getEvents(`from=${from}&to=${to}&mine=1`).then(evs => setUpcoming(evs.filter(e => e.status !== 'CANCELLED').slice(0, 5))).catch(() => {});
            api.getMyUnsettledEvents().then(l => setToSettle(l.filter(e => !e.rejectedEntryId))).catch(() => {}); // T153: odrzucone — osobna karta
          }
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, [isWorker]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>Ładowanie...</div>;

  return (
    <div className="flex flex-col gap-14">
      <div className="page-header">
        <h2 className="page-title">
          {isWorker ? `Witaj, ${user?.firstName} 👋` : isCoordinator ? 'Panel koordynatora' : isAdmin ? 'Panel administracyjny' : `Witaj, ${user?.firstName} 👋`}
        </h2>
        {canAddWarehouseHours && <Link to="/hours?new=1" className="btn btn-primary">+ Godziny</Link>}
        {hasExpenses && !isAdmin && <Link to="/expenses?new=1" className="btn btn-warning">+ Dodatek</Link>}
      </div>

      {(() => { // T161: eventy do potwierdzenia — na samej górze, mocnym kolorem
        const mine = (ev) => (ev.assignments || []).find(a => (a.user?.id || a.userId) === user?.id);
        const todo = upcoming.filter(ev => { const a = mine(ev); return a && !a.confirmedAt && !a.declinedAt; }); // T164: także po terminie (potwierdzić wciąż można)
        if (!todo.length) return null;
        return (
          <div className="card" style={{ border: '2px solid #F59E0B', background: '#FFFBEB', boxShadow: '0 0 0 4px #FEF3C7' }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 800, color: '#92400E' }}>🔔 Potwierdź udział w {todo.length === 1 ? 'evencie' : `${todo.length} eventach`}</h3>
            <div style={{ fontSize: 12, color: '#78350F', marginBottom: 8 }}>Dodano Cię do składu. Potwierdź albo zgłoś, że nie możesz — koordynator czeka na odpowiedź.</div>
            {todo.map(ev => (
              <div key={ev.id} className="flex gap-8" style={{ alignItems: 'center', padding: '6px 0', borderTop: '1px solid #FDE68A', fontSize: 12, flexWrap: 'wrap' }}>
                <span style={{ minWidth: 90 }}>{new Date(ev.date).toLocaleDateString('pl-PL', { weekday: 'short', day: '2-digit', month: '2-digit' })}</span>
                <b style={{ flex: 1, minWidth: 140 }}>{ev.name}</b>
                <span style={{ color: '#92400E' }}>{ev.startTime ? `${ev.startTime}${ev.endTime ? '–' + ev.endTime : ''}` : ''}{ev.confirmUntil ? (new Date(ev.confirmUntil) < new Date() ? ' · termin minął — potwierdź mimo to' : ` · do ${new Date(ev.confirmUntil).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`) : ''}</span>
                <Link className="btn btn-warning btn-sm" to="/events">Potwierdź →</Link>
              </div>
            ))}
          </div>
        );
      })()}
      {inbox.length > 0 && (
        <div className="card" style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700 }}>✉️ Ostatnie wiadomości</h3>
          {inbox.map(m => (
            <div key={m.id} style={{ padding: '6px 0', borderTop: '1px solid #FDE68A', fontSize: 12 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                <b style={{ fontWeight: m.readAt ? 600 : 800 }}>{m.title}</b>
                {!m.readAt && <span style={{ fontSize: 9, background: '#EF4444', color: '#fff', borderRadius: 99, padding: '0 6px' }}>nowa</span>}
                <span style={{ marginLeft: 'auto', fontSize: 10, color: '#64748B' }}>{fullName(m.sender)} · {new Date(m.createdAt).toLocaleDateString('pl-PL')}</span>
              </div>
              <div style={{ color: '#334155', whiteSpace: 'pre-wrap' }}>{m.content.length > 200 ? m.content.slice(0, 200) + '…' : m.content}</div>
            </div>
          ))}
          <div style={{ marginTop: 6, fontSize: 11 }}><Link to="/messages">Wszystkie wiadomości →</Link></div>
        </div>
      )}
      {isManagement && <PendingDocuments />}
      {seesStaffAbsences && <TodayAbsent />}
      {canAssignEvents && <TeamChanges />}
      {user?.role === 'WAREHOUSE' && <NormCard s={norm} />}

      <div className="flex gap-8 flex-wrap">
        {isManagement && (
          <>
            {canApproveLeaves && <StatCard label="Urlopy do zatw." value={pendingLeaves.length} accent="#F59E0B" />}
            <StatCard label="Godziny do zatw." value={pendingHours.length} accent="#2563EB" />
            {canReviewHours && <StatCard label="Dodatki do zatw." value={pendingBonuses.length} accent="#F59E0B" sub={pendingBonuses.length ? `${pendingBonuses.reduce((a, b) => a + Number(b.amount || 0), 0).toFixed(0)} zł` : undefined} />}
          </>
        )}
        {isWorker && (
          <>
            {hasLeave && <StatCard label="Urlop pozostały" value={`${fmtLeaveDays(leaveLeft({ leaveDaysTotal: 26, ...user }))} ${(leaveLeft({ leaveDaysTotal: 26, ...user }) % 1) ? '' : 'dni'}`} accent="#059669" />}
          </>
        )}
      </div>

      {/* T87: zarobki — do wypłaty od ostatniego rozliczenia (niezatwierdzone szarym), ten miesiąc, ostatnia wypłata */}
      {canAddHours && pay && (
        <div className="flex gap-8 flex-wrap">
          <PayCard label={pay.since ? `Do wypłaty od ${new Date(pay.since).toLocaleDateString('pl-PL')}` : 'Do wypłaty'}
            approved={pay.toPay} pending={pay.pending} accent="#059669" />
          <PayCard label="Zarobione w tym miesiącu" approved={pay.month.approved} pending={pay.month.pending} accent="#2563EB" />
          <div className="card stat-card" style={{ minWidth: 170 }}>
            <div className="stat-label">Ostatnia wypłata</div>
            {pay.last
              ? <><div className="stat-value" style={{ color: '#7C3AED' }}>{pay.last.amount.toFixed(0)} zł</div>
                  <div className="stat-sub">za okres do {new Date(pay.last.upToDate).toLocaleDateString('pl-PL')}</div>
                  <div style={{ fontSize: 11, marginTop: 4 }}><a href="#za-co" onClick={e => { e.preventDefault(); setShowLastPay(true); }}>za co? →</a> · <Link to="/account#wyplaty">wszystkie wypłaty</Link></div></>
              : <div className="stat-sub" style={{ marginTop: 6 }}>jeszcze nie było rozliczenia w aplikacji</div>}
          </div>
        </div>
      )}


      {isManagement && pendingLeaves.length > 0 && (
        <div className="card">
          <h3 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700 }}>Wnioski urlopowe do zatwierdzenia</h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Pracownik</th><th>Typ</th><th>Okres</th><th>Dni</th><th>Status</th><th>Akcja</th></tr></thead>
              <tbody>
                {pendingLeaves.slice(0, 5).map(l => (
                  <tr key={l.id}>
                    <td><PersonCell name={fullName(l.user)} /></td>
                    <td>{leaveLabel(l.leaveType)}</td>
                    <td>{new Date(l.startDate).toLocaleDateString('pl-PL')} – {new Date(l.endDate).toLocaleDateString('pl-PL')}</td>
                    <td>{l.hours ? `${l.hours} h` : l.totalDays}</td>
                    <td><Badge status={l.status} /></td>
                    <td>
                      <div className="flex gap-4">
                        {l.overlap && <span title={overlapText(l.overlap)} style={{ fontSize: 10, color: '#B45309', marginRight: 4 }}>⚠ {overlapShort(l.overlap)}</span>}
                        <button className="btn btn-success btn-sm" onClick={() => { if (l.overlap && !window.confirm(overlapText(l.overlap) + '\n\nZatwierdzić mimo to?')) return; api.approveLeave(l.id).then(() => setPendingLeaves(p => p.filter(x => x.id !== l.id))).catch(e => window.alert(e.message)); }}>✓</button>
                        <button className="btn btn-danger btn-sm" onClick={() => api.rejectLeave(l.id).then(() => setPendingLeaves(p => p.filter(x => x.id !== l.id)))}>✗</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isManagement && pendingHours.length > 0 && (
        <div className="card">
          <div className="flex" style={{ alignItems: 'baseline', marginBottom: 10 }}>
            <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>Godziny do zatwierdzenia ({pendingHours.length})</h3>
            {canApproveEventHours && !isAdmin && <span style={{ marginLeft: 8, fontSize: 10, color: '#7C3AED' }}>🎪 w tym eventowe wszystkich</span>}
            <Link to="/hours?scope=team" style={{ marginLeft: 'auto', fontSize: 11 }}>Wszystkie →</Link>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Pracownik</th><th>Data</th><th>Typ</th><th>Godziny</th><th>Kwota</th><th>Akcja</th></tr></thead>
              <tbody>
                {pendingHours.slice(0, 8).map(h => (
                  <tr key={h.id}>
                    <td><PersonCell name={fullName(h.user)} /></td>
                    <td>{new Date(h.date).toLocaleDateString('pl-PL')}<div><AddedOn date={h.date} createdAt={h.createdAt} /></div></td>
                    <td>{workLabel(h.workType)}{h.event && <div style={{ fontSize: 10, color: '#2563EB' }}>🎪 {h.event.name}</div>}</td>
                    <td>{h.workType === 'EVENT_DAILY' ? 'dniówka' : `${Number(h.totalHours)}h`}<TeamDiffBadge tc={h.teamCompare} /></td>
                    <td>{Number(h.totalAmount).toFixed(0)} zł</td>
                    <td>
                      <div className="flex gap-4">
                        <button className="btn btn-success btn-sm" onClick={() => api.approveHours(h.id).then(() => setPendingHours(p => p.filter(x => x.id !== h.id))).catch(e => window.alert(e.message))}>✓</button>
                        <button className="btn btn-danger btn-sm" onClick={() => { const r = askRejectReason(); if (r !== null) api.rejectHours(h.id, r).then(() => setPendingHours(p => p.filter(x => x.id !== h.id))).catch(e => window.alert(e.message)); }}>✗</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {canReviewHours && pendingBonuses.length > 0 && (
        <div className="card">
          <div className="flex" style={{ alignItems: 'baseline', marginBottom: 10 }}>
            <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>Dodatki i wydatki do zatwierdzenia ({pendingBonuses.length})</h3>
            <Link to="/expenses?scope=team" style={{ marginLeft: 'auto', fontSize: 11 }}>Wszystkie →</Link>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Pracownik</th><th>Data</th><th>Rodzaj</th><th>Kwota</th><th>Opis</th><th>Akcja</th></tr></thead>
              <tbody>
                {pendingBonuses.slice(0, 8).map(b => (
                  <tr key={b.id}>
                    <td><PersonCell name={fullName(b.user)} /></td>
                    <td>{new Date(b.date).toLocaleDateString('pl-PL')}<div><AddedOn date={b.date} createdAt={b.createdAt} /></div></td>
                    <td>{BONUS_LABELS[b.bonusType] || b.bonusType}{b.event && <div style={{ fontSize: 10, color: '#2563EB' }}>🎪 {b.event.name}</div>}</td>
                    <td><b>{Number(b.amount).toFixed(0)} zł</b></td>
                    <td style={{ fontSize: 11, color: '#64748B' }}>
                      {b.description || '—'}
                      {b.attachments?.map(a => <button key={a.id} className="btn btn-ghost btn-sm" style={{ padding: '0 4px', fontSize: 11 }} onClick={() => api.openAttachment(a.id).catch(() => {})}>📎</button>)}
                    </td>
                    <td>
                      <div className="flex gap-4">
                        <button className="btn btn-success btn-sm" onClick={() => api.approveBonus(b.id).then(() => setPendingBonuses(p => p.filter(x => x.id !== b.id))).catch(e => window.alert(e.message))}>✓</button>
                        <button className="btn btn-danger btn-sm" onClick={() => { const r = askRejectReason(); if (r !== null) api.rejectBonus(b.id, r).then(() => setPendingBonuses(p => p.filter(x => x.id !== b.id))).catch(e => window.alert(e.message)); }}>✗</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showLastPay && pay?.last && (
        <Modal title={`Wypłata ${pay.last.amount.toFixed(2).replace('.', ',')} zł — za okres do ${new Date(pay.last.upToDate).toLocaleDateString('pl-PL')}`} onClose={() => setShowLastPay(false)} wide>
          <SettlementSpec id={pay.last.id} bare />
        </Modal>
      )}

      {rejected.length > 0 && (
        <div className="card" style={{ border: '2px solid #FCA5A5', background: '#FEF2F2' }}>
          <h3 style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 700, color: '#991B1B' }}>❌ Odrzucone rozliczenia ({rejected.length})</h3>
          <div style={{ fontSize: 11, color: '#7F1D1D', marginBottom: 6 }}>Popraw i wyślij ponownie — albo usuń, jeśli odrzucenie jest słuszne.</div>
          {rejected.map(x => {
            // dokąd prowadzi „Popraw”: godziny pod eventem → okno rozliczenia; wydatek pod eventem / eventowy → „Odrzucone” w Moich eventach
            const to = x.kind === 'hours'
              ? (x.eventId ? `/events?settle=${x.eventId}` : '/hours')
              : (x.eventId || isEventOnly ? '/events?tab=rejected' : '/expenses'); // T155
            return (
              <div key={x.kind + x.id} className="flex gap-8" style={{ alignItems: 'center', padding: '6px 0', borderTop: '1px solid #FECACA', fontSize: 12, flexWrap: 'wrap' }}>
                <span style={{ minWidth: 80 }}>{new Date(x.date).toLocaleDateString('pl-PL')}</span>
                <b style={{ flex: 1, minWidth: 160 }}>
                  {x.kind === 'hours' ? `⏱ ${x.workType === 'EVENT_DAILY' ? 'dniówka' : `${Number(x.totalHours)} h`}` : `🧾 ${BONUS_LABELS[x.bonusType] || x.bonusType} ${Number(x.amount).toFixed(0)} zł`}
                  {x.event && <span style={{ fontWeight: 400, color: '#2563EB' }}> · 🎪 {x.event.name}</span>}
                  <div style={{ fontWeight: 400, fontSize: 11, color: '#991B1B' }}>
                    odrzucił(a) {x.approvedBy ? `${x.approvedBy.firstName} ${x.approvedBy.lastName || ''}` : '—'}{x.rejectReason ? <>: „{x.rejectReason}”</> : ''}
                  </div>
                </b>
                <Link className="btn btn-danger btn-sm" to={to}>Popraw</Link>
              </div>
            );
          })}
        </div>
      )}

      {toSettle.length > 0 && (
        <div className="card" style={{ border: '1px solid #BFDBFE', background: '#EFF6FF' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: '#1E40AF' }}>⏱ Do rozliczenia ({toSettle.length})</h3>
          <div style={{ fontSize: 11, color: '#475569', marginBottom: 6 }}>Eventy, na których byłeś, a nie wpisałeś jeszcze godzin.</div>
          {toSettle.map(ev => (
            <div key={ev.id} className="flex gap-8" style={{ alignItems: 'center', padding: '6px 0', borderTop: '1px solid #BFDBFE', fontSize: 12 }}>
              <span style={{ minWidth: 80 }}>{new Date(ev.date).toLocaleDateString('pl-PL')}</span>
              <b style={{ flex: 1 }}>{ev.name}</b>
              <span style={{ color: '#64748B' }}>{ev.location || ''}</span>
              <Link className="btn btn-primary btn-sm" to={`/events?settle=${ev.id}`}>Rozlicz</Link>
            </div>
          ))}
        </div>
      )}

      {((hasEventWork && isWorker) || seesAllEvents) && (upcoming.length > 0 || weekAll.length > 0 || seesAllEvents) && (() => {
        // T94: „Moje" (w składzie, 60 dni, max 5) / „Wszystkie" (7 dni, max 15 + podsumowanie) — dla widzących wszystkie
        const tab = seesAllEvents ? (hasEventWork && isWorker ? evTab : 'all') : 'mine';
        const mineOf = (ev) => (ev.assignments || []).find(a => (a.user?.id || a.userId) === user?.id);
        const shown = weekAll.slice(0, WEEK_LIMIT);
        const sum = weekAll.reduce((acc, ev) => {
          const f = eventFlags(ev);
          if (f.declined) acc.declined += 1;
          if (f.unconfirmed) acc.unconfirmed += 1;
          if (f.docs) acc.docs += 1;
          return acc;
        }, { declined: 0, unconfirmed: 0, docs: 0 });
        let lastDay = null;
        return (
          <div className="card">
            <div className="flex gap-8" style={{ alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>🎪 Najbliższe eventy</h3>
              {seesAllEvents && hasEventWork && isWorker && (
                <div className="filter-pills" style={{ marginLeft: 8 }}>
                  <button className={`filter-pill ${tab === 'mine' ? 'active' : ''}`} onClick={() => setEvTab('mine')}>Moje ({upcoming.length})</button>
                  <button className={`filter-pill ${tab === 'all' ? 'active' : ''}`} onClick={() => setEvTab('all')}>Wszystkie — 7 dni ({weekAll.length})</button>
                </div>
              )}
            </div>

            {tab === 'mine' && (upcoming.length === 0
              ? <div style={{ fontSize: 12, color: '#94A3B8' }}>Nie masz zaplanowanych eventów w najbliższych 60 dniach.</div>
              : <div className="table-wrap">
                  <table className="data-table">
                    <thead><tr><th>Data</th><th>Event</th><th>Miejsce</th><th>Czas</th><th>Mój udział</th></tr></thead>
                    <tbody>
                      {upcoming.map(ev => {
                        const a = mineOf(ev);
                        return (
                          <tr key={ev.id}>
                            <td>{dayLabel(ev.date)}{dayLabel(ev.date).length < 6 && <span style={{ color: '#94A3B8' }}> {new Date(ev.date).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' })}</span>}</td>
                            <td><b>{ev.name}</b></td>
                            <td>{ev.location || '—'}</td>
                            <td>{ev.startTime ? `${ev.startTime} – ${ev.endTime || '?'}` : '—'}</td>
                            <td style={{ fontSize: 11 }}>{a?.confirmedAt ? <span style={{ color: '#059669' }}>✓ potwierdzony</span>
                              : a?.declinedAt ? <span style={{ color: '#DC2626' }}>✗ odmówiłem</span>
                              : <Link to="/events">do potwierdzenia →</Link>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>)}

            {tab === 'all' && (
              <>
                <div style={{ fontSize: 12, color: '#334155', marginBottom: 8 }}>
                  W ciągu 7 dni: <b>{weekAll.length}</b> {weekAll.length === 1 ? 'event' : (weekAll.length % 10 >= 2 && weekAll.length % 10 <= 4 && (weekAll.length % 100 < 12 || weekAll.length % 100 > 14)) ? 'eventy' : 'eventów'}
                  {sum.declined > 0 && <span style={{ color: '#DC2626' }}> · {sum.declined} z odmowami</span>}
                  {sum.unconfirmed > 0 && <span style={{ color: '#B45309' }}> · {sum.unconfirmed} bez kompletu potwierdzeń</span>}
                  {sum.docs > 0 && <span style={{ color: '#7C3AED' }}> · {sum.docs} bez kompletu dokumentów</span>}
                </div>
                {weekAll.length === 0 ? <div style={{ fontSize: 12, color: '#94A3B8' }}>Brak eventów w najbliższych 7 dniach.</div> : (
                  <div className="table-wrap">
                    <table className="data-table">
                      <thead><tr><th>Dzień</th><th>Czas</th><th>Event</th><th>Miejsce</th><th>Skład</th><th>Uwagi</th></tr></thead>
                      <tbody>
                        {shown.map(ev => {
                          const f = eventFlags(ev);
                          const dl = dayLabel(ev.date);
                          const firstOfDay = dl !== lastDay; lastDay = dl;
                          return (
                            <tr key={ev.id} style={firstOfDay ? { borderTop: '2px solid #E2E8F0' } : undefined}>
                              <td style={{ fontWeight: firstOfDay ? 700 : 400, color: firstOfDay ? '#1E293B' : '#CBD5E1', whiteSpace: 'nowrap' }}>{firstOfDay ? dl : '〃'}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>{ev.startTime ? `${ev.startTime}–${ev.endTime || '?'}` : '—'}</td>
                              <td><b>{ev.name}</b>{mineOf(ev) && <span style={{ fontSize: 10, color: '#2563EB' }}> · jestem w składzie</span>}</td>
                              <td>{ev.location || '—'}</td>
                              <td>{f.team} os.</td>
                              <td style={{ fontSize: 11 }}>
                                {f.declined > 0 && <span style={{ color: '#DC2626' }} title="Odmówili udziału">✗ {f.declined} odmowa </span>}
                                {f.unconfirmed > 0 && <span style={{ color: '#B45309' }} title="Nie potwierdzili udziału">⏳ {f.unconfirmed} niepotw. </span>}
                                {f.docs > 0 && <span style={{ color: '#7C3AED' }} title="Event wymaga dokumentów, a ktoś ich nie ma">🪪 {f.docs} bez dok.</span>}
                                {!f.declined && !f.unconfirmed && !f.docs && <span style={{ color: '#059669' }}>✓</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {weekAll.length > WEEK_LIMIT && <div style={{ marginTop: 8, fontSize: 11 }}>+ {weekAll.length - WEEK_LIMIT} więcej — <Link to="/events">zobacz wszystkie w Eventach →</Link></div>}
              </>
            )}
            {tab === 'mine' && <div style={{ marginTop: 8, fontSize: 11 }}><Link to="/events">Wszystkie moje eventy →</Link></div>}
          </div>
        );
      })()}

      {isWorker && (!isEventOnly || user?.warehouseWorkEnabled) && recentHours.length > 0 && (
        <div className="card">
          <h3 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700 }}>Twoje ostatnie wpisy</h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Data</th><th>Typ</th><th>Czas</th><th>Godziny</th><th>Kwota</th><th>Status</th></tr></thead>
              <tbody>
                {recentHours.map(h => (
                  <tr key={h.id}>
                    <td>{new Date(h.date).toLocaleDateString('pl-PL')}</td>
                    <td>{workLabel(h.workType)}</td>
                    <td>{h.startTime ? `${h.startTime} – ${h.endTime}` : '—'}</td>
                    <td>{h.workType === 'EVENT_DAILY' ? 'dniówka' : `${Number(h.totalHours)}h`}</td>
                    <td>{Number(h.totalAmount).toFixed(0)} zł</td>
                    <td><Badge status={h.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
